/* Hover popup showing cited feed item previews for a highlighted summary segment.
   Rendered via portal to escape overflow:hidden ancestors. */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Plus, Check } from "lucide-react";
import { formatDuration } from "@/lib/formatDuration";
import type { SummaryCitation } from "../../../convex/aiSummary";

interface CitationWidgetProps {
  citations: SummaryCitation[];
  queuedIds: Set<string>;
  onQueue: (e: React.MouseEvent, feedItemId: string) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function CitationWidget({ citations, queuedIds, onQueue, anchorRef, onMouseEnter, onMouseLeave }: CitationWidgetProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    let left = rect.left;
    // Clamp so popup doesn't overflow right edge
    const maxLeft = window.innerWidth - Math.min(480, window.innerWidth * 0.9) - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;
    setPosition({ top: rect.bottom + 4, left });
  }, [anchorRef]);

  if (citations.length === 0 || !position) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ top: position.top, left: position.left }}
      className="fixed z-50 w-max max-w-[min(480px,90vw)] rounded-lg border border-border bg-popover p-2 shadow-lg"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-col gap-1.5 max-h-80 overflow-y-auto">
        {citations.map((citation) => {
          const isQueued = queuedIds.has(citation.feedItemId);
          return (
            <div
              key={citation.feedItemId}
              className={`flex items-center gap-2.5 rounded-md p-1.5 transition-colors hover:bg-muted ${isQueued ? "opacity-50" : ""}`}
            >
              <a
                href={citation.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 min-w-0 flex-1"
              >
                {citation.thumbnail ? (
                  <img
                    src={citation.thumbnail}
                    alt=""
                    className={`size-10 shrink-0 rounded object-cover ${isQueued ? "grayscale" : ""}`}
                  />
                ) : (
                  <div className="size-10 shrink-0 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                    {(citation.type ?? "item").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium leading-tight line-clamp-2 ${isQueued ? "text-muted-foreground" : ""}`}>
                    {citation.title}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    {citation.type && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                        {citation.type}
                      </Badge>
                    )}
                    {citation.source && <span className="truncate">{citation.source}</span>}
                    {citation.duration != null && citation.duration > 0 && (
                      <span className="flex items-center gap-0.5 shrink-0">
                        <Clock className="size-3" />
                        {formatDuration(citation.duration)}
                      </span>
                    )}
                  </div>
                </div>
              </a>
              {isQueued ? (
                <span className="shrink-0 flex items-center gap-1 text-xs text-primary font-medium px-1.5">
                  <Check className="size-3" />
                  In Queue
                </span>
              ) : (
                <Button
                  variant="default"
                  size="icon-sm"
                  className="shrink-0 active:scale-95"
                  onClick={(e) => onQueue(e, citation.feedItemId)}
                >
                  <Plus className="size-3.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
